import { motion } from 'framer-motion';
import { Sprout, Leaf, Info, AlertTriangle } from 'lucide-react';

function Section({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 text-gray-800">
        <Icon className="w-5 h-5 text-emerald-600" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="pl-7 text-gray-600">
        {children}
      </div>
    </div>
  );
}

export function ResultCard({ plantName }) {
  // Parse the AI response into sections
  const sections = plantName.split(/\d+\./).reduce((acc, section) => {
    if (!section.trim()) return acc;
    
    const lines = section.trim().split('\n');
    const title = lines[0].split(':')[0].trim();
    const content = lines.slice(1).join('\n').trim();
    
    acc[title] = content || lines[0].split(':')[1]?.trim() || '';
    return acc;
  }, {});

  const commonName = sections['Common and Scientific Name'] || 'Plant identification in progress...';
  const description = sections['Brief Description'] || '';
  const characteristics = sections['Key Characteristics for Identification'] || '';
  const careRequirements = sections['Basic care requirements'] || '';
  const warnings = sections['Any special notes or warnings'] || '';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-lg shadow-lg overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-emerald-100 p-2 rounded-full">
            <Sprout className="w-6 h-6 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Plant Identification</h2>
        </div>
        
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-emerald-800 mb-2">{commonName}</h3>
            {description && (
              <p className="text-emerald-700 text-sm leading-relaxed">{description}</p>
            )}
          </div>

          {characteristics && (
            <Section title="Characteristics" icon={Leaf}>
              <ul className="list-disc list-inside space-y-2">
                {characteristics.split(/[.,]/).filter(Boolean).map((char, i) => (
                  <li key={i} className="text-sm leading-relaxed">{char.trim()}</li>
                ))}
              </ul>
            </Section>
          )}

          {careRequirements && (
            <Section title="Care Requirements" icon={Info}>
              <div className="bg-blue-50/50 rounded-lg p-3">
                <p className="text-sm leading-relaxed text-blue-800">{careRequirements}</p>
              </div>
            </Section>
          )}

          {warnings && (
            <Section title="Important Notes" icon={AlertTriangle} className="bg-amber-50/50 rounded-lg p-4">
              <p className="text-sm leading-relaxed text-amber-800">{warnings}</p>
            </Section>
          )}
        </div>
      </div>
    </motion.div>
  );
}